import type { IntlShape } from 'react-intl';

import {
  type AgentCoreActor,
  AgentCoreApiError,
  type AgentCoreContext,
} from '@/shared/api/agentCore/agentCoreApi';

import { patientAgentMessages } from '../config';

export const buildActor = (
  userID: string,
  language?: string,
): AgentCoreActor => ({
  userID,
  language: language || 'en',
});

export const getCanonicalContext = (
  patientID: string,
  organizationID?: string,
): AgentCoreContext => ({
  patient_id: patientID,
  organization_id: organizationID || '',
});

export const getErrorMessage = (error: unknown, intl: IntlShape) => {
  const message =
    error instanceof Error || error instanceof AgentCoreApiError
      ? error.message
      : '';
  if (
    /invoke durable|connection refused|dial tcp|network error|context_length_exceeded|input tokens exceed/i.test(
      message,
    )
  ) {
    return intl.formatMessage(patientAgentMessages.serviceUnavailable);
  }
  if (error instanceof AgentCoreApiError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return intl.formatMessage(patientAgentMessages.unknownError);
};

export const getStringValue = (value: unknown) =>
  typeof value === 'string' ? value : '';

export const isOrganizationMismatchError = (error: unknown) => {
  if (error instanceof AgentCoreApiError) {
    return error.message.includes('does not belong to organization');
  }

  if (error instanceof Error) {
    return error.message.includes('does not belong to organization');
  }

  return false;
};

export const shortID = (value: string) =>
  value.length > 14 ? `${value.slice(0, 10)}…${value.slice(-4)}` : value;

export const formatMessageHoverStamp = (value: string, intl: IntlShape) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isToday) {
    return intl.formatTime(date, { hour: 'numeric', minute: '2-digit' });
  }

  return `${intl.formatDate(date, {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === now.getFullYear()
      ? {}
      : { year: 'numeric' as const }),
  })}, ${intl.formatTime(date, { hour: 'numeric', minute: '2-digit' })}`;
};

export const waitFor = (milliseconds: number) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

export const createMessageID = (prefix: string) =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
